import React, { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { $isDiffHunkNode } from '../customNodes/DiffHunkNode/DiffHunkNode';
import { SAVE_COMMAND } from './SavePlugin/SavePlugin';

type AllHunksResolvedPluginProps = {
  onAllDiffsResolved: (markdown: string) => void;
};

/**
 * Watches the Lexical tree for DiffHunkNode removal. When the last hunk is
 * accepted or rejected (count drops from >0 to 0), exports the resolved
 * markdown via SAVE_COMMAND and calls back to persist the result.
 */
export const AllHunksResolvedPlugin: React.FC<AllHunksResolvedPluginProps> = ({
  onAllDiffsResolved,
}) => {
  const [editor] = useLexicalComposerContext();
  const callbackRef = useRef(onAllDiffsResolved);
  callbackRef.current = onAllDiffsResolved;
  const previousCountRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has('historic') || tags.has('collaboration')) return;

      let shouldDispatch = false;
      editor.getEditorState().read(() => {
        const root = $getRoot();
        const count = root
          .getChildren()
          .filter(node => $isDiffHunkNode(node)).length;

        if (previousCountRef.current === null) {
          previousCountRef.current = count;
          return;
        }

        // A new batch of diff hunks arrived — reset so this cycle can fire.
        if (previousCountRef.current === 0 && count > 0) {
          firedRef.current = false;
        }

        if (!firedRef.current && previousCountRef.current > 0 && count === 0) {
          shouldDispatch = true;
        }

        previousCountRef.current = count;
      });

      if (shouldDispatch) {
        firedRef.current = true;
        editor.dispatchCommand(SAVE_COMMAND, async (markdown: string) => {
          callbackRef.current(markdown);
        });
      }
    });
  }, [editor]);

  return null;
};
